import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PrimaryBtn } from '../Assets/Buttons';
import { Field, Label } from '../Assets/Fieldset';
import { Input } from '../Assets/Input';
import { Checkbox, CheckboxField } from '../Assets/Checkbox';
import { Text, TextLink } from '../Assets/Text';
import { Heading } from '../Assets/Heading';
import { useSignUpMutation } from '../../services/api/userApi';

const SignUpForm = () => {
    const [signUp, { data: signUpData, isLoading: isSigningUp, isError: isSignUpError, error: signUpError }] = useSignUpMutation();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [step, setStep] = useState(1);
    const [errs, setErrs] = useState("");
    const [validated, setValidated] = useState(false);

    const navigate = useNavigate();
  
    // function to validate form fields 
    const validateForm = (event) => {
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            event.preventDefault();
            event.stopPropagation();
        } else {
            handleSubmit(event);
        }
        setValidated(true);
    };

    const handleSubmit = async(event) => {
        event.preventDefault();
        // body of post request
        const body = {
            username: username, 
            password: password,
            email: email
        };

        try {
            await signUp(body).unwrap();
            setStep(2);
        } catch (error) {
            console.log(error);
            if (error.data.error.includes("UsernameExistsException")) {
                setErrs("This username is already taken");
            } else if (error.data.error.includes("Email address is already in use")) {
                setErrs("Email address is already in use");
            } else if (error.data.error.includes("InvalidPasswordException")) {
                setErrs("Invalid password, please make sure all criteria are met");
            } else {
                setErrs("An error occurred during signup. Please try again.");
            }
        }
    };

    switch (step) {
        case 1:
            return (
                <form onSubmit={validateForm} className="grid w-full max-w-sm grid-cols-1 gap-6" noValidate>
                    <Heading>Create your account</Heading>
          
                    <Field>
                        <Label>Username</Label>
                        <Input 
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            minLength={4}
                            required
                            aria-invalid={username.length < 4 && validated ? "true" : "false"}
                        />
                        {username.length < 4 && validated && (
                            <Text className="text-brand-red-500 text-sm mt-1">Username must be at least 4 characters.</Text>
                        )}
                    </Field>
          
                    <Field>
                        <Label>Email</Label>
                        <Input 
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)} 
                            required
                            aria-invalid={email === "" && validated ? "true" : "false"}
                        />
                        {email === "" && validated && (
                            <Text className="text-brand-red-500 text-sm mt-1">Please enter a valid email address.</Text>
                        )}
                    </Field>
                
                    <Field>
                        <Label>Password</Label>
                        <Input 
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            pattern="(?=.*\d)(?=.*[!@#$%^&?*])(?=.*[a-z])(?=.*[A-Z]).{8,}"
                            aria-invalid={password !== "" && !password.match(/(?=.*\d)(?=.*[!@#$%^&?*])(?=.*[a-z])(?=.*[A-Z]).{8,}/) && validated ? "true" : "false"}
                        />
                        {password !== "" && !password.match(/(?=.*\d)(?=.*[!@#$%^&?*])(?=.*[a-z])(?=.*[A-Z]).{8,}/) && validated && (
                            <Text className="text-brand-red-500 text-sm mt-1">Password must contain at least 8 characters, including uppercase and lowercase letters, numbers, and special characters.</Text>
                        )}
                    </Field>
          
                    <CheckboxField>
                        <Checkbox required />
                        <Label>I accept these <TextLink href="/tos">terms and conditions</TextLink></Label>
                    </CheckboxField>
                
                    {errs && (
                        <Text className="text-brand-red-500">{errs}</Text>
                    )}
          
                    <PrimaryBtn type="submit" className="w-full">
                        Create account
                    </PrimaryBtn>
          
                    <Text>
                        Already have an account?{' '}
                        <TextLink href="/signin">Sign in</TextLink>
                    </Text>
                </form>
            );
      
        case 2:
            return (
                <div className="grid w-full max-w-sm grid-cols-1 gap-6 text-center">
                    <Heading>Thank you for signing up!</Heading>
                    <Text>Verify your email address</Text>
                    <Text>Make sure to check your junk/spam folders if you do not see the confirmation email shortly.</Text>
                    <PrimaryBtn type="button" onClick={() => navigate('/signin')} className="w-full">
                        Sign In
                    </PrimaryBtn>
                </div>
            );
    }
};

export default SignUpForm;